import { get, post, put, delete as deleteRequest } from '../../../services/api';
import { API_CONFIG } from '../../../config/api.config';
import {
  TrainsInPortReport,
  BillingReportItem,
  EurogateReport,
  ReportGenerationRequest,
  ReportGenerationResponse,
  ReportMetadata,
  ScheduledReport,
  ReportFilter,
  ReportExportOptions
} from '../types';

// Legacy Report Generation APIs (deprecated in favor of direct data export)
export async function generateReport(
  request: ReportGenerationRequest
): Promise<ReportGenerationResponse> {
  return post<ReportGenerationResponse, ReportGenerationRequest>(
    '/v1/reports/generate',
    request
  );
}

export async function getReportStatus(
  reportId: string
): Promise<ReportGenerationResponse> {
  return get<ReportGenerationResponse>(`/v1/reports/${reportId}/status`);
}

export async function getReportData(reportId: string): Promise<any[]> {
  return get<any[]>(`/v1/reports/${reportId}/data`);
}

// Trains-in-Port Report APIs
export async function fetchTrainsInPortData(
  filters?: ReportFilter
): Promise<TrainsInPortReport[]> {
  const params = new URLSearchParams();
  if (filters?.dateFrom) params.append('dateFrom', filters.dateFrom);
  if (filters?.dateTo) params.append('dateTo', filters.dateTo);
  if (filters?.operator) params.append('operator', filters.operator);
  if (filters?.trainNumber) params.append('trainNumber', filters.trainNumber);

  const queryString = params.toString();
  const url = `/v1/reports/trains-in-port${
    queryString ? `?${queryString}` : ''
  }`;

  return get<TrainsInPortReport[]>(url);
}

export async function fetchTrainsInPortFilterOptions(): Promise<{
  operators: string[];
  trainNumbers: string[];
}> {
  return get<{ operators: string[]; trainNumbers: string[] }>(
    '/v1/reports/trains-in-port/filter-options'
  );
}

// Billing Report APIs
export async function fetchBillingReportData(
  filters?: ReportFilter
): Promise<BillingReportItem[]> {
  const params = new URLSearchParams();
  if (filters?.dateFrom) params.append('dateFrom', filters.dateFrom);
  if (filters?.dateTo) params.append('dateTo', filters.dateTo);
  if (filters?.operator) params.append('operator', filters.operator);
  if (filters?.status) params.append('status', filters.status);

  const queryString = params.toString();
  const url = `/v1/reports/billing${queryString ? `?${queryString}` : ''}`;

  return get<BillingReportItem[]>(url);
}

// Eurogate Report APIs
export async function fetchEurogateReportData(dateRange: {
  from: string;
  to: string;
}): Promise<EurogateReport[]> {
  return get<EurogateReport[]>(
    `/v1/reports/eurogate-daily?dateFrom=${dateRange.from}&dateTo=${dateRange.to}`
  );
}

// Export APIs - Direct data export (stateless)
export async function exportTrainsInPortData(
  data: TrainsInPortReport[],
  options: ReportExportOptions,
  title?: string
): Promise<Blob> {
  const response = await fetch(
    `${API_CONFIG.baseUrl}/v1/reports/export/trains-in-port`,
    {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        Authorization: `Bearer ${localStorage.getItem('token')}`
      },
      body: JSON.stringify({
        data,
        format: options.format,
        includeHeaders: options.includeHeaders,
        dateRange: options.dateRange,
        ...(title ? { title } : {})
      })
    }
  );

  if (!response.ok) {
    const errorText = await response.text();
    throw new Error(`Failed to export trains in port report: ${errorText}`);
  }

  return response.blob();
}

export async function exportBillingReportData(
  data: BillingReportItem[],
  options: ReportExportOptions
): Promise<Blob> {
  const response = await fetch(
    `${API_CONFIG.baseUrl}/v1/reports/export/billing`,
    {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        Authorization: `Bearer ${localStorage.getItem('token')}`
      },
      body: JSON.stringify({
        data,
        format: options.format,
        includeHeaders: options.includeHeaders,
        dateRange: options.dateRange
      })
    }
  );

  if (!response.ok) {
    const errorText = await response.text();
    throw new Error(`Failed to export billing report: ${errorText}`);
  }

  return response.blob();
}

// Legacy export API (for backwards compatibility)
export async function exportReport(
  reportId: string,
  options: ReportExportOptions
): Promise<Blob> {
  const params = new URLSearchParams();
  params.append('format', options.format);
  params.append('includeHeaders', options.includeHeaders.toString());
  if (options.dateRange?.from)
    params.append('dateFrom', options.dateRange.from);
  if (options.dateRange?.to) params.append('dateTo', options.dateRange.to);

  const response = await fetch(
    `${API_CONFIG.baseUrl}/v1/reports/${reportId}/export?${params.toString()}`,
    {
      method: 'GET',
      headers: {
        Authorization: `Bearer ${localStorage.getItem('token')}`
      }
    }
  );

  if (!response.ok) {
    const errorText = await response.text();
    throw new Error(`Failed to export report: ${errorText}`);
  }

  return response.blob();
}

// Legacy Report Management APIs (deprecated in favor of stateless approach)
export async function fetchReports(): Promise<ReportMetadata[]> {
  return get<ReportMetadata[]>('/v1/reports');
}

export async function deleteReport(reportId: string): Promise<void> {
  return deleteRequest<void>(`/v1/reports/${reportId}`);
}

// AI Processing APIs
export async function validateReportData(
  type: string,
  data: any[]
): Promise<{ isValid: boolean; errors: string[]; warnings: string[] }> {
  return post<{ isValid: boolean; errors: string[]; warnings: string[] }, any>(
    `/v1/reports/ai/validate/${type}`,
    { data }
  );
}

export async function normalizeReportData(
  type: string,
  data: any[]
): Promise<any[]> {
  return post<any[], any>(`/v1/reports/ai/normalize/${type}`, { data });
}

export async function analyzeTimestamps(
  data: TrainsInPortReport[]
): Promise<{ discrepancies: any[]; summary: string }> {
  return post<{ discrepancies: any[]; summary: string }, any>(
    '/v1/reports/ai/analyze-timestamps',
    { data }
  );
}
